/**
 * src/pages/OriginAndBeyond/originAndBeyondData.ts
 * Data definitions for the Origin & Beyond hub page:
 * - Four journey questions with detailed exploration items
 * - Cosmic time ribbon events spanning the 4 questions with exact footnotes
 * - How-to-read cards for the evidence framework
 * - Integrity note content
 * - Scholarly and archival sources [1..7]
 */

import type { TimelineEvent, SourceRef } from "@/lib/originBeyond/types";

export interface JourneyDetail {
  id: "origin" | "consciousness" | "death" | "future";
  questionNumber: number;
  title: string;
  subtitle: string;
  teaser: string;
  bullets: [string, string, string];
  href: string;
  themeColor: string;
  badgeLevel: "established" | "research" | "philosophy" | "speculative";
}

export const JOURNEY_DETAILS: JourneyDetail[] = [
  {
    id: "origin",
    questionNumber: 1,
    title: "Where Did We Come From?",
    subtitle: "From Quantum Fluctuations to Prebiotic Oceans",
    teaser:
      "Trace the cosmic arc from Planck-era expansion and primordial nucleosynthesis to star formation, planetary accretion, and the chemical dawn of terrestrial life.",
    bullets: [
      "Cosmic microwave background and the 13.8-billion-year timeline measured by Planck",
      "Big Bang nucleosynthesis forming light elements within the first three minutes",
      "Prebiotic organic chemistry and the transition from non-living matter to self-replication",
    ],
    href: "/WhereDidWeComeFromPage",
    themeColor: "var(--primary)",
    badgeLevel: "established",
  },
  {
    id: "consciousness",
    questionNumber: 2,
    title: "What Is Consciousness?",
    subtitle: "The Hard Problem, Neural Correlates, and Experience",
    teaser:
      "Investigate how subjective inner experience emerges from physical matter, contrasting neurobiological models with quantum mind proposals and philosophical frameworks.",
    bullets: [
      "The 'hard problem' formulated by Chalmers: why physical processing yields subjective experience",
      "Integrated Information Theory (IIT) and Global Neuronal Workspace empirical programs",
      "Contested quantum-mind conjectures (Orch OR) evaluated alongside classical neuroscience",
    ],
    href: "/WhatIsConsciousnessPage",
    themeColor: "hsl(215 100% 65%)",
    badgeLevel: "research",
  },
  {
    id: "death",
    questionNumber: 3,
    title: "What Happens After Death?",
    subtitle: "Thermodynamics, Clinical Resuscitation, and Transience",
    teaser:
      "Examine the physical dissolution of living systems through entropy, clinical brain-resuscitation studies, and how cultural traditions interpret mortality.",
    bullets: [
      "The second law of thermodynamics: biological matter disperses into atomic recycling",
      "Resuscitation science and near-death experiences (NDEs) as physiological phenomena under hypoxia",
      "Cross-cultural philosophical frameworks without false scientific claims of survival",
    ],
    href: "/WhatHappensAfterDeathPage",
    themeColor: "hsl(270 70% 65%)",
    badgeLevel: "philosophy",
  },
  {
    id: "future",
    questionNumber: 4,
    title: "Where Is Everything Going?",
    subtitle: "Cosmic Fates, Stellar Dissolution, and Deep Time",
    teaser:
      "Project the universe across billions to googols of years—from the vaporization of terrestrial oceans to stellar burn-out, black hole evaporation, and the heat death.",
    bullets: [
      "Sun brightening by ~10% per Gyr, evaporating oceans in roughly 1 to 1.5 billion years",
      "Accelerating cosmic expansion driven by dark energy, separating distant galaxies",
      "Adams & Laughlin deep-time epochs: Degenerate Era, Black Hole Era, and ultimate Heat Death",
    ],
    href: "/WhereIsEverythingGoingPage",
    themeColor: "hsl(186 100% 50%)",
    badgeLevel: "established",
  },
];

/**
 * Cosmic time ribbon connecting all four questions along a logarithmic-style normalized track (0 to 1).
 * Matches numbers required by batch spec:
 * Universe age: ~13.8 Gyr (Planck 2018)
 * Earth: ~4.54 Gyr
 * Earliest life: at least ~3.5 Gyr
 * Humans (H. sapiens): ~300,000 yr (Jebel Irhoud)
 * Sun brightening: ~10% per Gyr, oceans lost ~1-1.5 Gyr, red giant ~5 Gyr
 * Accelerating expansion: Riess 1998, Perlmutter 1999
 * Last stars: roughly 10^14 yr
 */
export const HUB_TIMELINE_EVENTS: TimelineEvent[] = [
  {
    id: "hub-evt-big-bang",
    time: "13.8 billion years ago",
    position: 0.04,
    title: "The Big Bang & Quantum Inflation",
    level: "established",
    refs: [1],
    body: "Spacetime expands from an ultra-dense, hot primordial state. Cosmic microwave background observations pin the age of our observable universe at approximately 13.8 billion years (13.797 ± 0.023 Gyr).",
  },
  {
    id: "hub-evt-earth",
    time: "4.54 billion years ago",
    position: 0.18,
    title: "Earth Accretion & Solar System Dawn",
    level: "established",
    refs: [3],
    body: "From the circumstellar debris disk of our young Sun (itself about 4.6 billion years old), terrestrial planets accrete, establishing early crust and volatile reservoirs.",
  },
  {
    id: "hub-evt-first-life",
    time: "At least 3.5 billion years ago",
    position: 0.32,
    title: "Earliest Terrestrial Life",
    level: "established",
    refs: [1],
    body: "Microbial stromatolite and isotopic evidence confirm metabolic life within hydrothermal and coastal settings, initiating billions of years of biological evolution.",
  },
  {
    id: "hub-evt-observer",
    time: "About 300,000 years ago",
    position: 0.46,
    title: "The Observer: Emergence of Conscious Inquiry",
    level: "established",
    refs: [5, 2],
    body: "Fossils from Jebel Irhoud mark anatomically modern Homo sapiens. Complex neural networks evolve the reflective capacity to formulate deep ontological questions: What is mind? Why is there something rather than nothing?",
  },
  {
    id: "hub-evt-mortality",
    time: "Present & Continuous",
    position: 0.58,
    title: "Entropy & Transience: Every Life Ends",
    level: "established",
    refs: [2],
    body: "For every conscious observer, biological coherence is temporary. Organisms maintain internal order by consuming free energy; at death, structural entropy increases as molecular bonds dissipate back into the environment.",
  },
  {
    id: "hub-evt-sun-red-giant",
    time: "Roughly 5 billion years from now",
    position: 0.72,
    title: "The Sun's Red Giant Phase",
    level: "established",
    refs: [3],
    body: "Brightening by ~10% every billion years, our Sun will evaporate Earth's oceans in roughly 1 to 1.5 billion years. In approximately 5 billion years, core hydrogen exhaustion expands the Sun into a red giant, likely engulfing inner planets.",
  },
  {
    id: "hub-evt-dark-energy",
    time: "Accelerating Universe",
    position: 0.84,
    title: "Cosmic Acceleration & Galaxy Isolation",
    level: "established",
    refs: [6, 7],
    body: "Discovered via Type Ia supernovae, dark energy accelerates cosmological expansion. Over tens of billions of years, un-gravitationally bound galaxies red-shift past our cosmic event horizon.",
  },
  {
    id: "hub-evt-last-stars",
    time: "Roughly 10¹⁴ years (100 Trillion Years)",
    position: 0.96,
    title: "The Degenerate Era & The Final Stars",
    level: "research",
    refs: [4],
    body: "Galactic gas reservoirs deplete and the longest-lived red dwarfs exhaust their nuclear fuel. Stars darken, leaving white dwarfs, neutron stars, and black holes drifting in a cooling cosmos.",
  },
];

export interface HowToReadCard {
  title: string;
  description: string;
  iconName: "badge" | "book" | "compass";
}

export const HOW_TO_READ_CARDS: HowToReadCard[] = [
  {
    title: "Rigorous Evidence Tiers",
    description:
      "Every claim, model, and scenario carries an explicit evidence badge—separating established physics from active research, philosophical reasoning, and speculative theory.",
    iconName: "badge",
  },
  {
    title: "Verifiable Scholarly Citations",
    description:
      "Every page concludes with peer-reviewed source literature with open-access links (arXiv, DOI, Stanford Encyclopedia of Philosophy) for transparent fact-checking.",
    iconName: "book",
  },
  {
    title: "Respect for Cultural Traditions",
    description:
      "Spiritual and philosophical traditions across history are treated with cultural integrity, framed objectively as lived traditions rather than empirical scientific claims.",
    iconName: "compass",
  },
];

/**
 * Sources 1 to 7 matching batch spec:
 * Planck 2018; SEP Consciousness; Schröder & Smith 2008; Adams & Laughlin 1997; Hublin et al. 2017; Riess et al. 1998; Perlmutter et al. 1999
 */
export const HUB_SOURCES: SourceRef[] = [
  {
    id: 1,
    citation:
      "Planck Collaboration: Aghanim, N., et al. (2020). Planck 2018 results. VI. Cosmological parameters. Astronomy & Astrophysics, 641, A6.",
    url: "https://arxiv.org/abs/1807.06209",
  },
  {
    id: 2,
    citation:
      "Van Gulick, R. (2021). Consciousness. The Stanford Encyclopedia of Philosophy (Winter 2021 Edition), Edward N. Zalta (ed.).",
    url: "https://plato.stanford.edu/entries/consciousness/",
  },
  {
    id: 3,
    citation:
      "Schröder, K.-P., & Smith, R. C. (2008). Distant future of the Sun and Earth revisited. Monthly Notices of the Royal Astronomical Society, 386(1), 155–163.",
    url: "https://arxiv.org/abs/0801.4031",
  },
  {
    id: 4,
    citation:
      "Adams, F. C., & Laughlin, G. (1997). A dying universe: the long-term fate and evolution of astrophysical objects. Reviews of Modern Physics, 69(2), 337–372.",
    url: "https://arxiv.org/abs/astro-ph/9701131",
  },
  {
    id: 5,
    citation:
      "Hublin, J.-J., et al. (2017). New fossils from Jebel Irhoud, Morocco and the pan-African emergence of Homo sapiens. Nature, 546(7657), 289–292.",
    url: "https://www.nature.com/articles/nature22336",
  },
  {
    id: 6,
    citation:
      "Riess, A. G., et al. (1998). Observational Evidence from Supernovae for an Accelerating Universe and a Cosmological Constant. The Astronomical Journal, 116(3), 1009–1038.",
    url: "https://arxiv.org/abs/astro-ph/9805201",
  },
  {
    id: 7,
    citation:
      "Perlmutter, S., et al. (1999). Measurements of Ω and Λ from 42 High-Redshift Supernovae. The Astrophysical Journal, 517(2), 565–586.",
    url: "https://arxiv.org/abs/astro-ph/9812133",
  },
];
